import { useRef, useEffect } from 'react';

/**
 * Get the previous value of the props or state. In the first render it will return undefined.
 * Implementation is based on https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 *
 * Example of usage:
 * ```
 * const [count, setCount] = useState(0);
 * const prevCount = usePrevious(count);
 * ```
 * @param value - variable to keep track of
 * @returns previous value for the tracked variable, or undefined in the first render
 */
export const usePrevious = <T,>(value: T): T | undefined => {
	const ref = useRef<T | undefined>();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

export const usePreviousPersistent = <T,>(value: T): T | undefined => {
	// initialise the ref with previous and current values
	const ref = useRef<{ value: T; prev: T | undefined }>({
		value,
		prev: undefined,
	});

	const current = ref.current.value;

	// if the value passed into hook doesn't match what we store as "current"
	// move the "current" to the "previous"
	// and store the passed value as "current"
	if (value !== current) {
		ref.current = {
			value,
			prev: current,
		};
	}

	// return the previous value only
	return ref.current.prev;
};

/**
 * Get the previous value of the props or state. In the first render it will return passed value.
 * Implementation is based on https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 *
 * Example of usage:
 * ```
 * const [count, setCount] = useState(0);
 * const prevCount = usePreviousWithInitial(count);
 * ```
 * @param value - variable to keep track of
 * @returns previous value for the tracked variable, or currently passed value in the first render
 */
export const usePreviousWithInitial = <T,>(value: T): T => {
	const ref = useRef<T>(value);
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};
